import { DateTime } from "luxon";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import { TripleShift, shiftNameToLetter } from "../../../types/Shift";
import { ErrorResponse } from "../../../DTOs/Common/ErrorResponse";
import { setShiftEditability } from "../../../state/reducers/shiftReducer";
import { settingsAjax } from "../../../ajax/SettingsAjax";
import { useGlobalModal } from "../../Contexts/GlobalModalContext";
import { ModalActions } from "../../Modals/ModalActions";
import { Button } from "../../Buttons/Button";

type ShiftLockStatusChangeModalProps = {
  isCurrentlyLocked: boolean
  selectedDate: DateTime;
  selectedShift: TripleShift;
};

const ShiftLockStatusChangeModal: React.FC<ShiftLockStatusChangeModalProps> = ({
  isCurrentlyLocked,
  selectedDate,
  selectedShift
}) => {
  const { hideModal } = useGlobalModal();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleConfirm = useCallback(async () => {
    const selectedShiftChar = shiftNameToLetter(selectedShift);
    let res;

    if (isCurrentlyLocked)
      res = await settingsAjax.unlockShift(selectedDate, selectedShiftChar);
    else
      res = await settingsAjax.relockShift(selectedDate, selectedShiftChar);

    if (!ErrorResponse.isErrorResponse(res) && res.data) {
      dispatch(setShiftEditability(res.data));
      hideModal();
    }
    else
      enqueueSnackbar(t("crumbs.lockFailed"), { variant: "error" });
  }, [dispatch, hideModal, isCurrentlyLocked, selectedDate, selectedShift, enqueueSnackbar, t])

  return (
    <>
      <h2 className="text-2xl font-semibold mb-4 text-theme-text">{isCurrentlyLocked ? t("crumbs.unlockShiftTitle") : t("crumbs.lockShiftTitle")}</h2>
      <p className="mb-4 text-theme-text">
        {isCurrentlyLocked
          ? t("crumbs.unlockShiftBody")
          : t("crumbs.lockShiftBody")}
      </p>
      <ModalActions className="!mt-12">
        <Button variant="muted" onClick={hideModal}>{t("actions.cancel")}</Button>
        <Button
          variant="info"
          isDefaultAction
          mnemonic={isCurrentlyLocked ? "u" : "l"}
          onClick={handleConfirm}
        >
          {isCurrentlyLocked ? t("actions.unlock") : t("actions.lock")}
        </Button>
      </ModalActions>
    </>
  )
};

export default ShiftLockStatusChangeModal;